import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { MapPin, Package, Phone, KeyRound, FileText, AlertTriangle, Building2 } from "lucide-react";
import { MovaraMark } from "@/components/Brand";
import client, { formatApiErrorDetail } from "@/lib/api";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_TONE = (s) => {
  const red = ["Delayed", "Missing Documents", "POD Missing", "Closeout Blocked"];
  const amber = ["Needs Attention", "Customer Update Due"];
  if (red.includes(s)) return "#DC2626";
  if (amber.includes(s)) return "#D97706";
  return "#059669";
};

export default function TrackDelivery() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    client.get(`/track/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || "This tracking link is invalid."))
      .finally(() => setLoading(false));
  }, [token]);

  const photos = (data?.documents || []).filter((d) => d.has_file);

  const shareText = data
    ? `MovaRaBoard delivery ${data.job_number}` +
      (data.end_recipient_owner ? ` — DELIVER DIRECTLY TO: ${data.end_recipient_owner}` : "") +
      ` — ${data.pickup_location} → ${data.delivery_location}. Track: ${typeof window !== "undefined" ? window.location.href : ""}`
    : "";
  const waHref = (phone) => {
    const digits = (phone || "").replace(/[^0-9]/g, "");
    if (!digits) return null;
    return `https://wa.me/${digits}${shareText ? `?text=${encodeURIComponent(shareText)}` : ""}`;
  };
  const receivers = data
    ? [
        {
          key: "primary", label: "Primary",
          name: data.primary_contact_name || data.onsite_contact_name,
          title: data.primary_contact_title,
          phone: data.primary_contact_phone || data.onsite_contact_phone,
        },
        {
          key: "backup", label: "Backup",
          name: data.backup_contact_name, title: data.backup_contact_title, phone: data.backup_contact_phone,
        },
      ].filter((r) => r.name)
    : [];
  const hasBackup = receivers.some((r) => r.key === "backup");

  return (
    <div className="min-h-screen bg-canvas">
      <div className="bg-terminal px-4 h-14 flex items-center gap-2 sticky top-0 z-10">
        <MovaraMark className="h-5 w-6 text-white" />
        <span className="text-white font-black tracking-tight text-[15px]">MovaRaBoard</span>
        <span className="ml-auto text-[10px] font-mono font-bold text-white/50 tracking-widest uppercase">Delivery Tracking</span>
      </div>

      <div className="max-w-md mx-auto px-4 py-5">
        {loading && <p className="text-sm text-ink-2 text-center py-10">Loading delivery…</p>}

        {!loading && error && (
          <div className="bg-white border-2 border-[#0B132B] rounded-md p-6 text-center shadow-[2px_2px_0px_0px_#0B132B]" data-testid="track-error">
            <AlertTriangle className="h-8 w-8 text-danger mx-auto mb-2" />
            <h1 className="font-heading text-lg font-bold text-ink">Link unavailable</h1>
            <p className="text-sm text-ink-2 mt-1">{error}</p>
          </div>
        )}

        {!loading && data && (
          <div className="bg-white border-2 border-[#0B132B] rounded-md p-4 shadow-[2px_2px_0px_0px_#0B132B]" data-testid="track-card">
            <div className="flex justify-between items-center pb-2 border-b border-slate-100">
              <span className="text-[10px] font-mono font-bold text-slate-500 uppercase tracking-wider">JOB #{data.job_number}</span>
              <span className="text-white text-[9px] font-black tracking-widest uppercase px-2 py-0.5 rounded" style={{ backgroundColor: STATUS_TONE(data.risk_status) }}>
                {data.current_stage}
              </span>
            </div>

            {data.cargo_description && (
              <div className="mt-3">
                <span className="text-[9px] font-mono font-bold text-[#D97706] tracking-wider uppercase block flex items-center gap-1"><Package className="h-3 w-3" /> Manifest Cargo</span>
                <h4 className="font-black text-sm text-[#0B132B] leading-tight mt-0.5">{data.cargo_description}</h4>
              </div>
            )}

            <div className="mt-3 grid grid-cols-2 gap-2 bg-[#F8FAFC] p-2.5 rounded border border-slate-200/60">
              <div>
                <span className="text-[9px] font-mono font-bold text-slate-400 uppercase tracking-wider block">Property</span>
                <p className="text-xs font-extrabold text-[#0B132B] mt-0.5">{data.destination_property || data.customer_name || "—"}</p>
                <p className="text-[10px] text-slate-500 font-medium">{data.delivery_location}</p>
              </div>
              <div>
                <span className="text-[9px] font-mono font-bold text-slate-400 uppercase tracking-wider block">Type</span>
                {data.destination_type ? (
                  <span className="inline-flex items-center gap-1 mt-1 bg-slate-200 text-slate-800 text-[9px] font-bold px-1.5 py-0.5 rounded uppercase"><Building2 className="h-3 w-3" /> {data.destination_type}</span>
                ) : <p className="text-[10px] text-slate-400 mt-1">—</p>}
              </div>
            </div>

            {data.end_recipient_owner && (
              <div data-testid="track-deliver-to" className="mt-3 bg-[#0B132B] rounded-md p-3 shadow-[2px_2px_0px_0px_#D97706]">
                <span className="text-[9px] font-mono font-black text-[#D97706] tracking-widest uppercase block">Deliver Directly To:</span>
                <p className="text-white font-black text-sm leading-tight mt-0.5">{data.end_recipient_owner}</p>
              </div>
            )}

            {data.access_instructions && (
              <div className="mt-3 text-xs">
                <p className="text-slate-600 font-medium flex gap-1.5"><KeyRound className="h-3.5 w-3.5 text-[#0B132B] shrink-0 mt-0.5" /><span><strong className="text-[#0B132B] font-bold">Access:</strong> {data.access_instructions}</span></p>
              </div>
            )}

            {receivers.length > 0 && (
              <div className="mt-3 pt-3 border-t border-slate-100" data-testid="track-receivers">
                <span className="text-[9px] font-mono font-bold text-slate-400 uppercase tracking-wider block mb-2 flex items-center gap-1"><Phone className="h-3 w-3" /> Authorized Property Receivers</span>
                {hasBackup && (
                  <div data-testid="track-receiver-notice" className="mb-2.5 bg-amber-50 border border-amber-200 rounded p-2 text-[11px] font-semibold text-[#D97706]">
                    📣 NOTICE: If the Primary Purchasing Manager is unavailable, any authorized team member below can sign for and clear this cargo from the pier.
                  </div>
                )}
                <div className="space-y-2">
                  {receivers.map((r) => {
                    const wa = waHref(r.phone);
                    return (
                      <div key={r.key} data-testid={`track-receiver-${r.key}`} className="flex justify-between items-center bg-[#F8FAFC] p-2 rounded border border-slate-200">
                        <div>
                          <p className="text-xs font-extrabold text-[#0B132B]">{r.name} <span className="text-[9px] font-mono text-slate-400 uppercase">({r.label})</span></p>
                          {r.title && <p className="text-[10px] text-slate-500 font-medium">{r.title}</p>}
                        </div>
                        {wa && (
                          <a data-testid={`track-receiver-${r.key}-wa`} href={wa} target="_blank" rel="noreferrer"
                            className="bg-[#059669] text-white text-[10px] font-bold px-2.5 py-1.5 rounded shadow-sm hover:bg-[#047857] transition-colors shrink-0">
                            WhatsApp
                          </a>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {photos.length > 0 && (
              <div className="mt-3 pt-3 border-t border-slate-100">
                <span className="text-[9px] font-mono font-bold text-slate-400 uppercase tracking-wider block mb-2 flex items-center gap-1"><FileText className="h-3 w-3" /> Documents & Photos</span>
                <div className="space-y-1.5">
                  {photos.map((d) => (
                    <a key={d.type} data-testid={`track-doc-${d.type.replace(/\s+/g, "-")}`}
                      href={`${API}/api/track/${token}/documents/${encodeURIComponent(d.type)}/file`} target="_blank" rel="noreferrer"
                      className="flex items-center justify-between gap-2 border border-slate-200 rounded px-2.5 py-2 hover:border-[#0B132B] transition-colors">
                      <span className="text-[12px] text-ink font-medium truncate">{d.type}</span>
                      <span className="text-[10px] font-bold text-[#059669] uppercase shrink-0">View</span>
                    </a>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-1.5 text-[11px] text-slate-500">
              <MapPin className="h-3 w-3" /> {data.pickup_location} → {data.delivery_location}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
